import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Calendar, Plus, Building2, ClipboardCheck, AlertTriangle, Search, Filter, Eye, Play, CheckCircle, Clock, MapPin, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import VisiteDetail from '../components/Visites/VisiteDetail';
import CreateVisiteModal from '../components/Visites/CreateVisiteModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  'planifiee': 'bg-blue-100 text-blue-800',
  'en_cours': 'bg-yellow-100 text-yellow-800',
  'terminee': 'bg-green-100 text-green-800',
  'validee': 'bg-emerald-100 text-emerald-800',
  'annulee': 'bg-gray-100 text-gray-800'
};

const statusLabels = {
  'planifiee': 'Planifiée',
  'en_cours': 'En cours',
  'terminee': 'Terminée',
  'validee': 'Validée',
  'annulee': 'Annulée'
};

const typeLabels = {
  'periodique': 'Périodique',
  'ponctuelle': 'Ponctuelle',
  'urgente': 'Urgente',
  'conformite': 'Conformité'
};

export default function Visites() {
  const { user, token } = useAuth();
  const [visites, setVisites] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [buildingFilter, setBuildingFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedVisite, setSelectedVisite] = useState(null);

  const fetchVisites = useCallback(async () => {
    try {
      let url = `${API_URL}/api/visites`;
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (buildingFilter) params.append('building_id', buildingFilter);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVisites(data);
      }
    } catch (error) {
      console.error('Error fetching visites:', error);
      toast.error('Erreur lors du chargement des visites');
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, buildingFilter]);

  const fetchBuildings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/buildings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBuildings(data);
      }
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchVisites();
    fetchBuildings();
  }, [fetchVisites, fetchBuildings]);

  const handleCreateVisite = async (visiteData) => {
    try {
      const response = await fetch(`${API_URL}/api/visites`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(visiteData)
      });

      if (response.ok) {
        toast.success('Visite créée avec succès');
        setShowCreateModal(false);
        fetchVisites();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Error creating visite:', error);
      toast.error('Erreur lors de la création de la visite');
    }
  };

  const handleStartVisite = async (visiteId) => {
    try {
      const response = await fetch(`${API_URL}/api/visites/${visiteId}/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Visite démarrée');
        fetchVisites();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors du démarrage');
      }
    } catch (error) {
      toast.error('Erreur lors du démarrage de la visite');
    }
  };

  const filteredVisites = visites.filter(visite => {
    const matchesSearch = 
      visite.titre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      visite.building?.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      visite.building?.adresse?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canCreateVisite = ['gestionnaire', 'directeur_copro', 'admin'].includes(user?.role);

  if (selectedVisite) {
    return (
      <VisiteDetail
        visiteId={selectedVisite}
        onBack={() => {
          setSelectedVisite(null);
          fetchVisites();
        }}
        token={token}
      />
    );
  }

  return (
    <div className="p-6" data-testid="visites-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardCheck className="h-7 w-7 text-[#00D3A8]" />
            Visites d'immeuble
          </h1>
          <p className="text-gray-500 mt-1">Gérez les visites techniques et inspections de vos immeubles</p>
        </div>
        {canCreateVisite && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00BFA6] transition-colors"
            data-testid="create-visite-btn"
          >
            <Plus className="h-5 w-5" />
            Nouvelle visite
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une visite..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
              data-testid="search-visites"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] bg-white"
            data-testid="status-filter"
          >
            <option value="">Tous les statuts</option>
            <option value="planifiee">Planifiée</option>
            <option value="en_cours">En cours</option>
            <option value="terminee">Terminée</option>
            <option value="validee">Validée</option>
          </select>

          {/* Building Filter */}
          <select
            value={buildingFilter}
            onChange={(e) => setBuildingFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8] bg-white"
            data-testid="building-filter"
          >
            <option value="">Tous les immeubles</option>
            {buildings.map(building => (
              <option key={building.id} value={building.id}>
                {building.nom}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Calendar className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {visites.filter(v => v.statut === 'planifiee').length}
              </p>
              <p className="text-sm text-gray-500">Planifiées</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {visites.filter(v => v.statut === 'en_cours').length}
              </p>
              <p className="text-sm text-gray-500">En cours</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {visites.filter(v => v.statut === 'terminee' || v.statut === 'validee').length}
              </p>
              <p className="text-sm text-gray-500">Terminées</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {visites.reduce((sum, v) => sum + (v.anomalies_count || 0), 0)}
              </p>
              <p className="text-sm text-gray-500">Anomalies</p>
            </div>
          </div>
        </div>
      </div>

      {/* Visites List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8]"></div>
          </div>
        ) : filteredVisites.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="w-20 h-20 bg-gradient-to-br from-slate-50 to-slate-100 rounded-full flex items-center justify-center mb-6 shadow-inner">
              <ClipboardCheck className="text-slate-300" size={36} strokeWidth={1.5} />
            </div>
            <h3 className="text-xl font-semibold text-slate-700 mb-2 text-center">Aucune visite planifiée</h3>
            <p className="text-slate-500 text-sm max-w-lg mx-auto mb-4 text-center leading-relaxed">
              Les visites d'immeuble permettent de documenter l'état des parties communes et d'identifier les travaux nécessaires.
            </p>
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-6 max-w-md">
              <div className="flex items-start gap-3">
                <div className="p-1.5 bg-amber-100 rounded-lg">
                  <HelpCircle size={14} className="text-amber-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-amber-800 mb-1">Conseil</p>
                  <p className="text-xs text-amber-700">Planifiez des visites régulières (trimestrielles) pour un suivi efficace de l'état de l'immeuble.</p>
                </div>
              </div>
            </div>
            {canCreateVisite && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-2.5 bg-[#00D3A8] text-white rounded-xl text-sm font-medium hover:bg-[#00B892] transition-all shadow-sm hover:shadow-md"
              >
                + Planifier une visite
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredVisites.map(visite => (
              <div
                key={visite.id}
                className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => setSelectedVisite(visite.id)}
                data-testid={`visite-item-${visite.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-gray-900">{visite.titre}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[visite.statut] || 'bg-gray-100'}`}>
                        {statusLabels[visite.statut] || visite.statut}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                        {typeLabels[visite.type_visite] || visite.type_visite}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Building2 className="h-4 w-4" />
                        {visite.building?.nom || 'Immeuble non défini'}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {visite.building?.adresse || '-'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {formatDate(visite.date_planifiee)}
                      </span>
                    </div>
                    {visite.anomalies_count > 0 && (
                      <div className="mt-2 flex items-center gap-1 text-sm text-orange-600">
                        <AlertTriangle className="h-4 w-4" />
                        {visite.anomalies_count} anomalie(s) détectée(s)
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {visite.statut === 'planifiee' && canCreateVisite && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartVisite(visite.id);
                        }}
                        className="p-2 text-[#00D3A8] hover:bg-[#00D3A8]/10 rounded-lg transition-colors"
                        title="Démarrer la visite"
                      >
                        <Play className="h-5 w-5" />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedVisite(visite.id);
                      }}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Voir les détails"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateVisiteModal
          buildings={buildings}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateVisite}
          token={token}
        />
      )}
    </div>
  );
}
